import type { RabbitIncidentMessage } from '@collector/queue-gateway-shared-types'
import {
  isEmergencyIncident,
  isIncidentCancelled,
  isIncidentDeleted,
} from '@collector/shared-ui-domain'
import { Incident } from '@collector/sportsapi-client'
import { useSameTimeIncidents } from '@desktop/views/Relation/Sports/composables'
import {
  IncidentsGroup,
  isIncidentsGroup,
} from '@desktop/views/Relation/Sports/configuration/incidentsGroup'

import { createIncidentTimeMap } from './incidentTimeMap'

export function useSameTimeIncidentsMap(): {
  hasSameTimeIncidents: (incident: Incident) => boolean
  addIncidentsTimeToIncidentsIdMap: (
    incidents: (Incident | IncidentsGroup)[],
  ) => boolean
  updateIncidentsTimeToIncidentsIdMap: (
    incidentUpdates: RabbitIncidentMessage[],
  ) => boolean
} {
  // helpers
  const {
    hasSameTimeIncidents,
    addIncidentTimeToIncidentsIdMap,
    updateIncidentTimeToIncidentsIdMap,
    deleteIncidentTimeToIncidentsIdMap,
  } = createIncidentTimeMap()

  // composables
  const { sameTimeExcludedIncidents, sameTimeExcludedIncidentEventStatuses } =
    useSameTimeIncidents()

  // methods
  function isAllowedIncident(incident: Incident): boolean {
    return (
      !sameTimeExcludedIncidents.has(incident.incident_id) &&
      !sameTimeExcludedIncidentEventStatuses.has(incident.status_id) &&
      !isIncidentDeleted(incident) &&
      !isIncidentCancelled(incident) &&
      !isEmergencyIncident(incident)
    )
  }

  /**
   * @returns `true` if time of the incident is shared with at least one other incident
   */
  function addIncidentsTimeToIncidentsIdMap(
    incidents: (Incident | IncidentsGroup)[],
  ): boolean {
    let changed = false

    for (const incident of incidents) {
      const group = isIncidentsGroup(incident) ? incident.incidents : [incident]

      if (group.every(isAllowedIncident)) {
        changed = addIncidentTimeToIncidentsIdMap(incident) || changed
      }
    }

    return changed
  }

  /**
   * @returns `true` if time of the incident is shared with at least one other incident
   */
  function updateIncidentsTimeToIncidentsIdMap(
    incidentUpdates: RabbitIncidentMessage[],
  ): boolean {
    let changed = false

    for (const incidentUpdate of incidentUpdates) {
      if (incidentUpdate.data.action === 'delete') {
        changed =
          deleteIncidentTimeToIncidentsIdMap(incidentUpdate.data) || changed
      }

      if (incidentUpdate.data.action === 'update') {
        if (isAllowedIncident(incidentUpdate.data)) {
          changed =
            updateIncidentTimeToIncidentsIdMap(incidentUpdate.data) || changed
        } else {
          changed =
            deleteIncidentTimeToIncidentsIdMap(incidentUpdate.data) || changed
        }
      }
    }

    return changed
  }

  return {
    hasSameTimeIncidents,
    addIncidentsTimeToIncidentsIdMap,
    updateIncidentsTimeToIncidentsIdMap,
  }
}
